/**
 * Manifest Review x facility rounds (April Torino's feedback via Isaiah Cotto, 2026-10-27, approved).
 * docs/manifest/MANIFEST_REVIEW.md; server: services/manifest/reviewRounds.js, addBack.js.
 *
 *   SeenFact          on each resident line: "Seen by Dana 10:42 AM" and the provider's outcome that visit day, or
 *                     "Not seen on the round"; a closed review's unseen lines say "Not seen at visit".
 *   ReviewLineFilter  Seen by provider / Not seen / Undecided, above the residents.
 *   CloseNotSeen      "Close the N lines the provider did not see": once every SEEN resident has a decision, the unseen
 *                     ones are closed as "Not seen at visit" (recorded once on the close, with who; no per-line
 *                     disposition, and never "Not a TWL"). A confirm dialog states the count. Without seen marks the
 *                     review closes the ordinary way only, and the screen says so.
 *   AddBackControl    a resident off the Current manifest (screened DNQ, annual not due) is put back on the NEXT
 *                     manifest, with an optional reason of at most 100 characters.
 *
 * Manifest review stays an after-the-visit tool: nothing here changes a decision already recorded, a testing status, a
 * tag or the DNQ words. Before the server has these pieces (an older server, migrations not applied) nothing renders.
 */
import React, { useId, useRef, useState } from 'react';
import { ListPlus } from 'lucide-react';
import { apiRequest } from '../../services/api';
import { Button } from '../../components/new-components/ui/button';
import { Dialog, DialogOverlay, DialogPanel, DialogPortal, DialogTitle, useReturnFocus } from '../../components/shadcn/ui/dialog';
import type { ReviewDetail, ReviewRounds, ResidentLine } from './manifestApi';
import { OFFICE_REASON_MAX } from './OfficePriority';
import { easternTime } from '../provider/roundProgress';
import './manifest-rounds.css';

/** Whether this review can say "seen" or "not seen" at all: the rounds are on and the provider marked someone. */
export const roundsUsed = (r: ReviewRounds | null | undefined): boolean => Boolean(r && r.ready && r.seen_lines > 0);

/** The provider's round on one line. */
export function SeenFact({ line, rounds }: { line: ResidentLine; rounds: ReviewRounds | null | undefined }) {
  if (line.closed_not_seen) {
    return <span className="mf-tag quiet mf-notseen-closed" title="Closed with the review: the provider did not see them on that visit day. No decision was recorded.">Not seen at visit</span>;
  }
  if (!rounds || !rounds.ready) return null;
  const s = line.seen;
  if (s) {
    const t = easternTime(s.at);
    return (
      <span className="mf-seen" title="The provider's round on this visit day. The decision is still yours.">
        <span className="mf-seen-mark" aria-hidden="true">{'✓'}</span>
        Seen by {s.first_name}{t ? ` ${t}` : ''}{s.source === 'TWL_Q' ? ' · auto' : ''}
        {s.outcome ? <span className="mf-seen-outcome"> · {s.outcome.label}</span> : null}
      </span>
    );
  }
  // "Not seen" says something only when the provider used rounds at this visit, and only of a resolved resident: a
  // line eMerge never matched to a resident is neither seen nor not seen (it says "Identity unresolved").
  if (!roundsUsed(rounds) || !line.patient_id) return null;
  return <span className="mf-tag quiet mf-notseen" title="Nobody marked this resident seen on the provider's round that visit day.">Not seen on the round</span>;
}

export type LineFilter = 'all' | 'seen' | 'notseen' | 'undecided';

export const isUndecided = (l: ResidentLine) => !l.disposition && !l.closed_not_seen;

export function applyLineFilter(lines: ResidentLine[], f: LineFilter): ResidentLine[] {
  if (f === 'seen') return lines.filter((l) => Boolean(l.seen));
  if (f === 'notseen') return lines.filter((l) => Boolean(l.patient_id) && !l.seen);
  if (f === 'undecided') return lines.filter(isUndecided);
  return lines;
}

/** Seen by provider / Not seen / Undecided. The seen pair only when the provider used rounds at this visit. */
export function ReviewLineFilter({ lines, rounds, value, onChange }: {
  lines: ResidentLine[]; rounds: ReviewRounds | null | undefined; value: LineFilter; onChange: (f: LineFilter) => void;
}) {
  const used = roundsUsed(rounds);
  const opts: { key: LineFilter; label: string }[] = [
    { key: 'all', label: `All ${lines.length}` },
    ...(used ? [
      { key: 'seen' as LineFilter, label: `Seen by provider ${applyLineFilter(lines, 'seen').length}` },
      { key: 'notseen' as LineFilter, label: `Not seen ${applyLineFilter(lines, 'notseen').length}` },
    ] : []),
    { key: 'undecided', label: `Undecided ${applyLineFilter(lines, 'undecided').length}` },
  ];
  return (
    <div className="mf-linefilter" role="group" aria-label="Show residents">
      {opts.map((o) => (
        <button key={o.key} type="button" className={`mf-linefilter-btn${value === o.key ? ' on' : ''}`} aria-pressed={value === o.key} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
      {rounds && rounds.ready && !used && (
        <span className="mf-quiet">The provider did not mark anyone seen on this visit&rsquo;s round.</span>
      )}
    </div>
  );
}

/**
 * A small modal confirm on the shadcn Dialog (Radix), role="alertdialog": Escape and the backdrop cancel, focus starts on
 * Cancel, Tab and Shift+Tab stay inside while it is open, the page behind is inert, and focus returns to what opened it
 * when it closes (`returnTo`, else the element focused when it opened). Nothing cancels it while the close is in flight.
 *
 * Escape is marked handled (Radix preventDefaults it), so the docked board's own Escape (DockTable: skips a handled
 * key) does not also close the review panel behind the dialog.
 */
function Confirm({ title, children, confirmLabel, busy, onConfirm, onCancel, returnTo }: {
  title: string; children: React.ReactNode; confirmLabel: string; busy: boolean; onConfirm: () => void; onCancel: () => void;
  returnTo?: React.RefObject<HTMLElement | null>;
}) {
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const opened = useReturnFocus(true);
  const bodyId = useId();
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogPortal>
        <div className="mf-confirm-root">
          <DialogOverlay className="z-0 bg-[rgba(15,30,35,0.45)]" />
          <DialogPanel
            className="mf-confirm"
            role="alertdialog"
            aria-describedby={bodyId}
            onOpenAutoFocus={(e) => { e.preventDefault(); cancelRef.current?.focus(); }}
            onCloseAutoFocus={(e) => {
              // Back to the trigger when it is still on the page (a successful close removes it: then nothing to return to).
              const el = returnTo && returnTo.current;
              if (el && el.isConnected) { e.preventDefault(); el.focus(); } else opened(e);
            }}
            onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
            onPointerDownOutside={(e) => { if (busy) e.preventDefault(); }}
          >
            <DialogTitle>{title}</DialogTitle>
            <div id={bodyId}>{children}</div>
            <div className="mf-confirm-acts">
              {/* Portalled under body, outside the board: a box-less .board wrapper so `.board .board-btn` still applies. */}
              <div className="board mf-confirm-board">
                <button type="button" className="board-btn board-btn--primary" disabled={busy} onClick={onConfirm}>{busy ? 'Closing…' : confirmLabel}</button>
                <button type="button" ref={cancelRef} className="board-btn" disabled={busy} onClick={onCancel}>Cancel</button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </DialogPortal>
    </Dialog>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "Close the N lines the provider did not see." Offered on an open review when the provider marked someone seen at
 * this visit; refused (disabled, with the reason) while a seen resident has no decision.
 */
export function CloseNotSeen({ detail, onClose }: {
  detail: ReviewDetail;
  onClose: (expectedNotSeen: number) => Promise<boolean>;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const r = detail.rounds;
  if (!r || !r.ready || detail.review.state !== 'OPEN') return null;
  if (!r.close_not_seen_available) {
    // Seen marks but the close not switched on (20261027j not applied): nothing to offer, nothing to explain.
    if (detail.counts.undecided === 0 || r.seen_lines > 0) return null;
    return (
      <p className="mf-quiet mf-closenotseen-none" role="note">
        The provider did not mark anyone seen on this visit&rsquo;s round, so every resident needs a decision before the
        review closes.
      </p>
    );
  }
  const n = r.not_seen_undecided;
  if (n === 0) return null;
  // Lines eMerge never matched to a resident are never "not seen": they need a decision first (the server agrees).
  const unresolved = r.unresolved_undecided || 0;
  const blocked = r.seen_undecided > 0 || unresolved > 0;
  return (
    <div className="mf-closenotseen">
      <button type="button" ref={triggerRef} className="board-btn" disabled={blocked} onClick={() => setAsking(true)}>
        Close the {plural(n, 'line', 'lines')} the provider did not see
      </button>
      <p className="mf-quiet">
        {r.seen_undecided > 0
          ? `${plural(r.seen_undecided, 'resident the provider saw has', 'residents the provider saw have')} no decision yet. Decide every seen resident first.`
          : unresolved > 0
            ? `${plural(unresolved, 'line is', 'lines are')} not matched to a resident and ${unresolved === 1 ? 'needs' : 'need'} a decision first: nobody can say whether the provider saw them.`
            : `Every resident the provider saw has a decision. The ${plural(n, 'resident', 'residents')} not seen on the round can close as "Not seen at visit": no decision is recorded for them, and they are not "Not a TWL".`}
      </p>
      {asking && (
        <Confirm
          title={`Close with ${plural(n, 'resident', 'residents')} not seen?`}
          confirmLabel={`Close the ${plural(n, 'line', 'lines')}`}
          busy={busy}
          returnTo={triggerRef}
          onCancel={() => setAsking(false)}
          onConfirm={async () => {
            setBusy(true);
            try { if (await onClose(n)) setAsking(false); } finally { setBusy(false); }
          }}
        >
          <p>
            This closes the review. The {plural(n, 'resident', 'residents')} the provider did not see on {detail.review.visit_date}
            {' '}keep no decision; the close records them as &ldquo;Not seen at visit&rdquo; with your name and the time.
          </p>
          <p className="mf-quiet">They are not marked &ldquo;Not a TWL&rdquo;. Decisions already recorded are unchanged.</p>
        </Confirm>
      )}
    </div>
  );
}

/** The closed review's sentence about its not-seen close, when it had one. */
export function ClosedNotSeenNote({ detail }: { detail: ReviewDetail }) {
  const c = detail.rounds && detail.rounds.closed_not_seen;
  if (!c || detail.review.state !== 'COMPLETED') return null;
  const t = easternTime(c.at);
  return (
    <span className="mf-closed-notseen">
      {' '}{plural(c.count, 'resident was', 'residents were')} not seen at the visit and closed without a decision
      {c.by ? ` by ${c.by}` : ''}{t ? ` at ${t}` : ''}.
    </span>
  );
}

export interface AddedBackView {
  reason: string | null;
  kind: 'PROVIDER' | 'OFFICE';
  by: { first_name: string; mine: boolean };
  at: string | null;
}

export const addBackApi = {
  set: (facilityId: string, patientId: string, add: boolean, reason?: string | null) =>
    apiRequest<{ patient_id: string; changed: boolean; added_back: AddedBackView | null }>(
      'POST', `/manifest-intake/facilities/${facilityId}/residents/${patientId}/add-back`,
      add ? { add, reason: reason && reason.trim() ? reason.trim() : null } : { add },
    ),
};

const whoAdded = (b: AddedBackView) => (b.by.mine ? 'you' : b.kind === 'OFFICE' ? `${b.by.first_name} (office)` : `${b.by.first_name} (provider)`);

/**
 * "Add back to the next manifest" on a resident line whose resident is off the Current manifest now (screened DNQ,
 * annual not due), and the marker with who and why once added back.
 */
export function AddBackControl({ line, facilityId, rounds, name, onChanged }: {
  line: ResidentLine; facilityId: string; rounds: ReviewRounds | null | undefined; name: string; onChanged: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!rounds || !rounds.add_back_ready || !line.patient_id) return null;
  const back = line.added_back || null;
  if (!back && !line.off_manifest) return null;
  const pid = line.patient_id;
  const send = async (add: boolean) => {
    setBusy(true); setError('');
    try {
      const r = await addBackApi.set(facilityId, pid, add, reason);
      if (r && r.success) {
        setEditing(false);
        onChanged(add ? `${name} is on the next manifest.` : `${name} is off the next manifest again.`);
        return;
      }
      setError((r && ((r as { message?: string }).message || r.error)) || 'eMerge did not confirm that. Nothing changed; try again.');
    } catch {
      setError('eMerge did not confirm that. Nothing changed; try again.');
    } finally { setBusy(false); }
  };
  return (
    <div className="mf-back">
      <button
        type="button"
        className={`mf-back-pill${back ? ' is-on' : ''}`}
        aria-pressed={back ? 'true' : 'false'}
        aria-expanded={editing}
        aria-label={back
          ? `${name} is added back to the next manifest${back.reason ? `: ${back.reason}` : ''}, by ${whoAdded(back)}. Change or take back.`
          : `Add ${name} back to the next manifest`}
        disabled={busy}
        onClick={() => { setReason(back && back.reason ? back.reason : ''); setEditing((e) => !e); }}
      >
        <ListPlus aria-hidden="true" /> {back ? 'Added back to the next manifest' : 'Add back to the next manifest'}
        {back && back.reason ? <small> · {back.reason}</small> : null}
        {back ? <small> · by {whoAdded(back)}</small> : null}
      </button>
      {!back && <span className="mf-quiet">Off the Current manifest: screened DNQ, annual not due.</span>}
      {error && <p role="alert" className="mf-prio-error">{error}</p>}
      {editing && (
        <form className="mf-back-editor" aria-label={`Add back: ${name}`} onSubmit={(ev) => { ev.preventDefault(); send(true); }}>
          <label htmlFor={`mf-back-${pid}`}>Why add them back (optional)</label>
          <input id={`mf-back-${pid}`} value={reason} maxLength={OFFICE_REASON_MAX} placeholder="e.g. family asked for a retest" onChange={(ev) => setReason(ev.target.value)} />
          <small className="mf-quiet">
            {reason.length}/{OFFICE_REASON_MAX} · on the Current manifest until a visit day after today ends with them seen or decided;
            a mark or a decision today, this review included, does not end it. Testing status, tags and the DNQ words are unchanged.
          </small>
          <div className="mf-prio-actions">
            <Button type="submit" disabled={busy}>{back ? 'Save reason' : 'Add back'}</Button>
            {back && <Button type="button" variant="outline" disabled={busy} onClick={() => send(false)}>Take off the next manifest</Button>}
            <Button type="button" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
